import entityMetaStore, { entityInfoComparator } from "data/meta/entityMetaStore";
import EntityName from "data/util/EntityName";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import TableColumnMetaStore from "data/meta/TableColumnMetaStore";

const implementationFieldsByEntity = (implKey, fieldFilter, indicatePartials=false) => {
    if (fieldFilter == null) indicatePartials = false;
    const entityInfos = entityMetaStore
        .getAllInfos(ei => ei.implKey === implKey);
    const orderedRootEntityKeys = entityInfos
        .filter(ei => ei.type === "ROOT")
        .sort(entityInfoComparator)
        .map(ei => ei.key);
    const orderedVersionKeys = orderedRootEntityKeys
        .map(k => new EntityName(k).versionKey);
    const fieldsByEntity = new Map(); // Map<baseEntityKey, Map<fieldKey, itemIdx>>
    const groupLookup = new Map(); // Map<bareEntityKey, Group>
    entityInfos
        .sort((a, b) => {
            const avk = new EntityName(a.key).versionKey;
            const bvk = new EntityName(b.key).versionKey;
            return orderedVersionKeys.indexOf(avk) - orderedVersionKeys.indexOf(bvk);
        })
        .forEach(ei => {
            const fieldInfos = entityFieldMetaStore
                .getAllInfos(ei.key, indicatePartials ? null : fi => {
                    if (!fieldFilter || fieldFilter(fi)) return true;
                    return fi.otherEntityKey && !TableColumnMetaStore.getInfoByKey(ei.logicalKey, fi.logicalKey).inverse;
                });
            if (fieldInfos.length === 0) return;
            const bareEntityKey = new EntityName(ei.key).bareEntityKey;
            let group;
            let fieldKeyIndexes = fieldsByEntity.get(bareEntityKey);
            if (fieldKeyIndexes == null) {
                fieldsByEntity.set(bareEntityKey, fieldKeyIndexes = new Map());
                group = {
                    key: bareEntityKey,
                    typePriority: ei.typePriority,
                    name: ei.name,
                    pluralName: ei.pluralName,
                    items: [],
                };
                groupLookup.set(group.key, group);
            } else {
                group = groupLookup.get(bareEntityKey);
            }
            fieldInfos.forEach(fi => {
                if (!fieldKeyIndexes.has(fi.key)) {
                    fieldKeyIndexes.set(fi.key, group.items.length);
                    if (indicatePartials) {
                        const filter = fieldFilter(fi);
                        fi = {
                            ...fi,
                            __any: filter,
                            __all: filter,
                        };
                    }
                    group.items.push(fi);
                } else if (indicatePartials) {
                    const filter = fieldFilter(fi);
                    const agg = group.items[fieldKeyIndexes.get(fi.key)];
                    if (agg.__all && !filter) {
                        agg.name += " *";
                    }
                    agg.__any = agg.__any || filter;
                    agg.__all = agg.__all && filter;
                }
            });
        });
    const groups = [];
    const doEntityKeys = entityKeys => entityKeys
        .map(ek => new EntityName(ek).bareEntityKey)
        .map(bek => { // IMPURE
            if (!groupLookup.has(bek)) return undefined;
            const g = groupLookup.get(bek);
            groupLookup.delete(bek);
            return g;
        })
        .filter(g => g) // it's a DAG, not a tree. and worse: multi-version
        .sort((a, b) => a.typePriority - b.typePriority)
        .forEach(g => {
            groups.push(g);
            g.items.sort((a, b) => {
                if (a.name !== b.name) {
                    return a.name < b.name ? -1 : 1;
                }
                return a.key < b.key ? -1 : 1;
            });
            doEntityKeys(g.items
                .filter(f => f.otherEntityKey)
                .map(f => f.otherEntityKey));
            // now do the full filter, which means we need to re-filter assocs,
            // while non-assocs that managed to get here are known to be good.
            if (fieldFilter) {
                g.items = g.items.filter(indicatePartials
                    ? it => it.__any
                    : it => !it.otherEntityKey || fieldFilter(it));
            }
        });
    doEntityKeys(orderedRootEntityKeys);
    return groups;
};

export default implementationFieldsByEntity;



// WEBPACK FOOTER //
// ./app/data/selectors/implementationFieldsByEntity.js